import { z } from 'zod';

import { aiProviderDescriptorSchema } from '../ai-catalog';
import { idSchema, isoDateTimeSchema } from '../common';
import { aiKeyValidationStatusSchema, aiProviderSchema } from '../enums';

/**
 * `/ai-provider` DTOs — bring-your-own-key configuration.
 *
 * The full API key travels in exactly one direction, exactly once: inbound on
 * `PUT /ai-provider`. From there it is encrypted with `AI_ENCRYPTION_KEY` (held
 * in env/KMS, never in the database) and written to `ai_provider_settings`,
 * which has no plaintext column. Only the AI worker decrypts it.
 *
 * **The API never returns the full key and never logs it.** Every response in
 * this file exposes `keyLastFour` only, so the dashboard can render `sk-…abcd`
 * and nothing more. There is deliberately no "reveal key" endpoint: a key that
 * cannot be read back cannot be leaked by a stolen session, and a user who has
 * lost their key rotates it rather than recovering it.
 *
 * EVERY PROVIDER-SPECIFIC FACT IN THIS RESOURCE COMES FROM THE CATALOG
 * (`../ai-catalog`), never from a literal in the dashboard. The settings screen
 * is built by iterating `GetAiProviderResponse.providers`; it contains no
 * mention of "OpenAI" and needs no change when a second provider is registered.
 */

/**
 * Wide bounds on purpose — provider key formats change, and a prefix check that
 * is too clever rejects a valid key. Verification is done by calling the
 * provider, not by pattern-matching. The catalog's `keyHint.prefix` is advisory
 * guidance for the form; it is deliberately not enforced here.
 */
export const rawApiKeySchema = z.string().trim().min(20).max(500);

/**
 * A provider's whole key pool, as the operator typed it.
 *
 * WHY A LIST AND NOT A STRING. The settings screen presents this as a textarea,
 * one key per line, because that is how an administrator receives them. But the
 * *transport* is an array: splitting on newlines is a parsing decision, and doing
 * it on the client means the server never has to guess whether a stray `\r`,
 * a trailing blank line or a line of spaces was meant to be a key. The client
 * splits, trims and drops blanks; the server receives exactly the keys.
 *
 * Order is meaningful and preserved: it is the order the pool scheduler tries
 * keys in, so an operator who puts their highest-quota key first gets it used
 * first whenever it is free.
 *
 * The 20-key ceiling matches `maxKeys` on the widest catalog entry and exists to
 * catch a paste accident — a whole secrets file dropped into the box — rather
 * than to express a policy. The per-provider limit is checked against the
 * catalog on the server, where it belongs.
 */
export const rawApiKeyListSchema = z.array(rawApiKeySchema).min(1).max(20);

// ---------------------------------------------------------------------------
// Validation outcome
// ---------------------------------------------------------------------------

/**
 * What happened when the server asked the provider about a key.
 *
 * `status` is the field to branch on. `message` is Vietnamese prose for a human
 * and is free to change, so the dashboard must never parse it. Neither field can
 * contain key material: the probe never puts the key anywhere but a request
 * header, and provider text is scrubbed before it reaches this shape.
 */
export const aiKeyValidationSchema = z.object({
  status: aiKeyValidationStatusSchema,
  /** True only for `VALID`. Convenience so the UI does not re-derive it. */
  ok: z.boolean(),
  message: z.string(),
  /** Which model the probe asked about — the one that was actually verified. */
  model: z.string(),
  /** HTTP status the provider answered with; null for network failures. */
  httpStatus: z.number().int().nullable(),
  latencyMs: z.number().int().nonnegative(),
  checkedAt: isoDateTimeSchema,
});
export type AiKeyValidation = z.infer<typeof aiKeyValidationSchema>;

// ---------------------------------------------------------------------------
// Stored settings
// ---------------------------------------------------------------------------

/**
 * One key inside a provider's pool.
 *
 * A row of its own rather than a string in a list, because a key in a pool has a
 * life: it is tried in a fixed position, it can be busy, it can be the one that
 * answered last, and it can be the only one of six that a probe rejected. All of
 * that has to be visible, and none of it can be attached to a bare string.
 *
 * **Never carries key material.** `keyLastFour` is the whole of what leaves the
 * server, exactly as for the single-key case.
 */
export const aiProviderApiKeySchema = z.object({
  id: idSchema,
  /** The only fragment of the key that ever leaves the server. */
  keyLastFour: z.string().length(4),
  /**
   * 1-based position in the pool — the order the scheduler tries keys in, and
   * the order the textarea shows them in, so "dòng thứ 3 bị từ chối" points at
   * the line the operator is looking at.
   */
  position: z.number().int().positive(),
  /**
   * Which encryption scheme/master key produced the stored ciphertext. Bumped
   * on rotation so old rows stay decryptable while new ones use the new key.
   */
  encryptionVersion: z.number().int().min(1),
  /** Per key, because a pool is exactly where one bad key hides among five good ones. */
  lastValidationStatus: aiKeyValidationStatusSchema.nullable(),
  lastValidationMessage: z.string().nullable(),
  lastValidatedAt: isoDateTimeSchema.nullable(),
  /** Set by the worker when a call using this key completes. */
  lastUsedAt: isoDateTimeSchema.nullable(),
  /**
   * Taken out of rotation without being deleted.
   *
   * The scheduler skips a disabled key; the operator keeps the evidence of which
   * key it was. Deleting instead would lose the last-four that identifies it in
   * the audit log.
   */
  disabledAt: isoDateTimeSchema.nullable(),
});
export type AiProviderApiKey = z.infer<typeof aiProviderApiKeySchema>;

export const aiProviderSettingsSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  provider: aiProviderSchema,
  /**
   * Every key stored for this provider, in scheduler order.
   *
   * Always at least one — a credential row with no keys is meaningless and the
   * server deletes it rather than storing it. Single-key providers simply have a
   * pool of one, so nothing downstream needs two shapes.
   */
  keys: z.array(aiProviderApiKeySchema).min(1),
  /**
   * Where this provider sits in the fallback order, 1 being tried first.
   *
   * The operator owns this: it is the answer to "provider nào ưu tiên hơn". A
   * call goes to the lowest-numbered provider that has a usable key, and falls
   * through to the next one on a failure that a retry against the same provider
   * could not fix.
   */
  priority: z.number().int().positive(),
  /** The model this workspace chose. Null means "the catalog default". */
  defaultModel: z.string().nullable(),
  /**
   * `defaultModel` with the catalog default already substituted, so the screen
   * never has to reimplement that fallback and get it subtly different from the
   * server.
   *
   * CAVEAT, and the UI must not paper over it: this is the workspace's *stored
   * preference*, not yet what the scoring worker runs. The worker currently
   * takes its model from the `AI_SCORING_MODEL` environment variable and does
   * not read this column. Until that changes, present this as "model đã chọn",
   * never as "model đang chạy".
   */
  effectiveModel: z.string(),
  /**
   * How many calls this provider can have in flight at once, across the pool.
   *
   * `keys.length × maxConcurrentCallsPerKey` from the catalog, computed here so
   * the screen can state the real ceiling instead of making an operator multiply
   * two numbers from two different places. For a serial provider this is simply
   * the number of keys, which is the sentence the textarea's help text needs:
   * paste a second key and you get a second lane.
   */
  maxConcurrentCalls: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type AiProviderSettings = z.infer<typeof aiProviderSettingsSchema>;

// ---------------------------------------------------------------------------
// GET /ai-provider
// ---------------------------------------------------------------------------

/**
 * Everything the settings screen needs, in one request.
 *
 * `providers` is the catalog — it is present whether or not anything is
 * configured, because the screen has to render the provider picker before the
 * first key exists. `settings` is a list because the table is unique on
 * (workspace, provider), so a workspace can hold credentials for several
 * providers at once.
 */
export const getAiProviderResponseSchema = z.object({
  /** The catalog. Drives the picker, the model list, the docs link, the placeholder. */
  providers: z.array(aiProviderDescriptorSchema).min(1),
  /** True when at least one non-revoked key is stored. */
  configured: z.boolean(),
  /** Every configured, non-revoked provider, already sorted by `priority`. */
  settings: z.array(aiProviderSettingsSchema),
  /**
   * The fallback order, most-preferred first — the same order as `settings`,
   * lifted out so a client can render or reorder it without walking the rows.
   *
   * A call is attempted against the first entry; if it fails in a way that
   * retrying the same provider cannot fix, the next entry is tried. An empty
   * array means nothing is configured and AI falls back to the platform key, or
   * stops.
   */
  fallbackOrder: z.array(aiProviderSchema),
  /**
   * True when the deployment has its own key, so AI still runs for a workspace
   * with no BYOK. The screen should not promise AI scoring when this is false
   * and nothing is configured.
   */
  platformKeyAvailable: z.boolean(),
});
export type GetAiProviderResponse = z.infer<typeof getAiProviderResponseSchema>;

// ---------------------------------------------------------------------------
// PUT /ai-provider
// ---------------------------------------------------------------------------

export const putAiProviderRequestSchema = z.object({
  provider: aiProviderSchema,
  /**
   * Plaintext keys — accepted here, encrypted before they touch storage, and
   * redacted by the logger. Never echoed back in any response.
   *
   * **This replaces the whole pool, it does not append to it.** A textarea is a
   * document, not a form field: what the operator sees when they press save is
   * what they mean to have stored, and a merge would leave keys alive that they
   * had just deleted from the box. Removing a compromised key has to be as
   * simple as deleting its line.
   *
   * The list must be non-empty — clearing every key is a revocation, and that is
   * `DELETE /ai-provider/:provider`, which also clears the ciphertext and writes
   * the audit entry that a revocation deserves.
   */
  apiKeys: rawApiKeyListSchema,
  /**
   * Model to use. Any string the provider accepts; the catalog list is a picker
   * convenience, not an allow-list. Omit to take the provider's catalog default.
   */
  model: z.string().trim().min(1).max(120).optional(),
  /**
   * Ask the provider to confirm the keys work before storing them.
   *
   * Defaults to true, and turning it off is an explicit act. Storing an unchecked
   * key means the workspace finds out it was wrong after hours of silently
   * skipped scoring, which is a failure mode this product has shipped more than
   * once. The escape hatch exists only for air-gapped installs where the
   * provider is unreachable from the API host but reachable from the worker.
   *
   * With a pool, **every** key is probed, not a sample. The whole point of a
   * pool is that work silently routes around a key that stopped working, so an
   * unchecked bad key among five good ones would show up as nothing worse than
   * a slightly slower queue — for months.
   */
  validateBeforeSave: z.boolean().default(true),
});
export type PutAiProviderRequest = z.infer<typeof putAiProviderRequestSchema>;

/** One key's probe outcome, tied to the line it came from. */
export const aiKeyValidationEntrySchema = z.object({
  /** 1-based, matching the textarea line so a rejection points at a row. */
  position: z.number().int().positive(),
  keyLastFour: z.string().length(4),
  validation: aiKeyValidationSchema,
});
export type AiKeyValidationEntry = z.infer<typeof aiKeyValidationEntrySchema>;

export const putAiProviderResponseSchema = z.object({
  provider: aiProviderSchema,
  /** The stored pool, in order. Last four only, as everywhere else. */
  keys: z.array(aiProviderApiKeySchema).min(1),
  /** The model that was stored, after the catalog default was applied. */
  model: z.string(),
  /** False when `model` is not in the catalog list — a hint, not an error. */
  modelKnown: z.boolean(),
  /** True when this replaced an existing pool rather than creating the first one. */
  rotated: z.boolean(),
  /** Empty when `validateBeforeSave` was false; one entry per key otherwise. */
  validations: z.array(aiKeyValidationEntrySchema),
  updatedAt: isoDateTimeSchema,
});
export type PutAiProviderResponse = z.infer<typeof putAiProviderResponseSchema>;

// ---------------------------------------------------------------------------
// POST /ai-provider/validate  (check without storing)
// ---------------------------------------------------------------------------

/**
 * Try a key without saving it.
 *
 * Separate from PUT because "does this key work?" and "make this the workspace's
 * key" are different decisions, and a user pasting a key into a form should be
 * able to ask the first without committing to the second. It exists on the way
 * *in* only — there is no endpoint that re-checks a key already in the database,
 * because doing so would require the API to decrypt one, and the API has no
 * decryption code at all.
 */
export const validateAiProviderRequestSchema = z.object({
  provider: aiProviderSchema,
  /** Same list semantics as PUT, so "Thử khoá" checks exactly what "Lưu" would store. */
  apiKeys: rawApiKeyListSchema,
  model: z.string().trim().min(1).max(120).optional(),
});
export type ValidateAiProviderRequest = z.infer<typeof validateAiProviderRequestSchema>;

export const validateAiProviderResponseSchema = z.object({
  provider: aiProviderSchema,
  /** One entry per submitted key, in submitted order. */
  validations: z.array(aiKeyValidationEntrySchema).min(1),
  /** True only when every key came back `VALID`. Saves the client a reduce. */
  allValid: z.boolean(),
});
export type ValidateAiProviderResponse = z.infer<typeof validateAiProviderResponseSchema>;

// ---------------------------------------------------------------------------
// PUT /ai-provider/priority  (reorder the fallback chain)
// ---------------------------------------------------------------------------

/**
 * Set the fallback order for the whole workspace.
 *
 * WHY THE WHOLE ORDER AND NOT A PRIORITY PER ROW. "Priority" is a property of
 * the *list*, not of any one provider: setting MidiAI to 1 is meaningless unless
 * something else moves to 2. Accepting a single `{provider, priority}` would let
 * two rows claim first place, and the server would then have to invent a
 * tie-break the operator never chose. Taking the ordered list makes the
 * malformed states unrepresentable — the array is the answer.
 *
 * Providers with no stored credential may be listed and are ignored; providers
 * that have one and are omitted keep their existing relative order, appended
 * after the named ones. That way a client built before a third provider existed
 * cannot accidentally demote it to nothing.
 */
export const putAiProviderPriorityRequestSchema = z.object({
  order: z.array(aiProviderSchema).min(1),
});
export type PutAiProviderPriorityRequest = z.infer<typeof putAiProviderPriorityRequestSchema>;

export const putAiProviderPriorityResponseSchema = z.object({
  /** The order as stored, after unknown entries were dropped and gaps closed. */
  fallbackOrder: z.array(aiProviderSchema),
  settings: z.array(aiProviderSettingsSchema),
});
export type PutAiProviderPriorityResponse = z.infer<typeof putAiProviderPriorityResponseSchema>;

// ---------------------------------------------------------------------------
// DELETE /ai-provider/:provider  (revoke)
// ---------------------------------------------------------------------------

export const deleteAiProviderResponseSchema = z.object({
  provider: aiProviderSchema,
  revokedAt: isoDateTimeSchema,
  /** How many keys were cleared. Zero on a repeat call, which is idempotent. */
  keysRevoked: z.number().int().nonnegative(),
  /** AI work falls back to the system key, or stops if none is configured. */
  fallbackToSystemKey: z.boolean(),
  /**
   * What is left to answer calls: the remaining providers in fallback order.
   *
   * Revoking the first provider in a chain is a very different act from revoking
   * the only one, and the response should say which just happened rather than
   * leaving the operator to re-read the settings screen to find out.
   */
  fallbackOrder: z.array(aiProviderSchema),
});
export type DeleteAiProviderResponse = z.infer<typeof deleteAiProviderResponseSchema>;
